import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Smartphone, RefreshCw, AlertCircle } from 'lucide-react';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const AdminDashboard = () => {
    const [devices, setDevices] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const fetchDevices = async () => {
        setLoading(true);
        setError('');
        try {
            const res = await axios.get(`${API_BASE_URL}/device`);
            setDevices(res.data || []);
        } catch (err) {
            setError('Failed to load device list. Ensure the backend is running at ' + API_BASE_URL);
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchDevices();
    }, []);

    return (
        <div className="animate-in fade-in duration-300">
            <header className="flex justify-between items-center mb-12">
                <div>
                    <h1 className="text-3xl font-bold">Admin Control Panel</h1>
                    <p className="text-slate-400 mt-1">Overview of all active devices in the network</p>
                </div>
                <button
                    onClick={fetchDevices}
                    className="flex items-center gap-2 bg-white/5 border border-white/10 text-white px-5 py-2.5 rounded-xl font-medium transition-all duration-200 hover:bg-white/10 hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={loading}
                >
                    <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
                    Refresh List
                </button>
            </header>

            {error && (
                <div className="p-4 mb-8 bg-red-400/10 border border-red-400/20 text-red-400 rounded-xl flex items-center gap-3 glass-card">
                    <AlertCircle size={20} />
                    <span>{error}</span>
                </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {loading && !devices.length ? (
                    Array(6).fill(0).map((_, i) => (
                        <div key={i} className="h-[100px] glass-card animate-pulse bg-gradient-to-r from-transparent via-white/5 to-transparent bg-[length:200%_100%]" />
                    ))
                ) : (
                    devices.length > 0 ? (
                        devices.map((deviceId, idx) => (
                            <div key={idx} className="p-6 flex items-center gap-5 glass-card transition-transform duration-200 hover:-translate-y-1 hover:border-primary group">
                                <div className="p-4 bg-primary/10 text-primary rounded-xl transition-colors duration-200 group-hover:bg-primary/20">
                                    <Smartphone size={32} />
                                </div>
                                <div className="flex-1">
                                    <h3 className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 font-bold">Device ID</h3>
                                    <p className="font-mono font-bold text-accent-cyan text-lg select-all">{deviceId}</p>
                                </div>
                                <div className="flex items-center gap-1.5 text-green-400 text-xs font-bold self-start mt-1">
                                    <span className="w-2 h-2 bg-green-400 rounded-full shadow-[0_0_8px_#4ade80]" />
                                    Active
                                </div>
                            </div>
                        ))
                    ) : !loading && (
                        <div className="col-span-full text-center py-16 text-slate-400 text-lg">
                            No devices found.
                        </div>
                    )
                )}
            </div>
        </div>
    );
};

export default AdminDashboard;
